import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Avatar,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import paymentService from '../../services/paymentService';
import userService from '../../services/userService';
import bankService from '../../services/bankService';
import api from '../../services/api';

const PaymentForm = ({ open, onClose, payment, onSuccess }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    payment_method: 'Efectivo',
    reference_number: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [owners, setOwners] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchYearsBack, setSearchYearsBack] = useState(3);

  // Cargar parámetros y bancos
  useEffect(() => {
    loadSystemParameters();
    loadBanks();
  }, []);

  useEffect(() => {
    if (open) {
      loadOwners();
      if (payment) {
        // Modo edición
        setFormData({
          user_id: payment.user_id || '',
          amount: payment.amount || '',
          payment_date: payment.payment_date ? payment.payment_date.split('T')[0] : '',
          month: payment.month || new Date().getMonth() + 1,
          year: payment.year || new Date().getFullYear(),
          payment_method: payment.payment_method || 'Efectivo',
          reference_number: payment.reference_number || '',
        });
        // Mostrar imagen actual si existe
        if (payment.payment_proof_url) {
          setImagePreview(`http://localhost:3001${payment.payment_proof_url}`);
        }
      } else {
        // Modo creación
        const today = new Date();
        setFormData({
          user_id: '',
          amount: '',
          payment_date: today.toISOString().split('T')[0],
          month: today.getMonth() + 1,
          year: today.getFullYear(),
          payment_method: 'Efectivo',
          reference_number: '',
        });
        setImageFile(null);
        setImagePreview(null);
      }
      setError('');
    }
  }, [payment, open]);

  const loadSystemParameters = async () => {
    try {
      const response = await api.get('/parameters');
      const params = response.data.parameters || [];
      const searchYearsParam = params.find(p => p.param_key === 'search_years_back');

      if (searchYearsParam) {
        setSearchYearsBack(parseInt(searchYearsParam.param_value) || 3);
      }
    } catch (err) {
      console.error('Error al cargar parámetros del sistema:', err);
      // Mantener el valor por defecto de 3
    }
  };

  const loadOwners = async () => {
    try {
      const response = await userService.getOwners();
      setOwners(response.owners || []);
    } catch (err) {
      console.error('Error al cargar propietarios:', err);
    }
  };

  const loadBanks = async () => {
    try {
      const response = await bankService.getActiveBanks();
      setBanks(response.banks || []);
    } catch (err) {
      console.error('Error al cargar bancos:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    // Validar que sea una imagen
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Solo se permiten archivos de imagen (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('La imagen no debe superar los 5MB');
      return;
    }

    setImageFile(file);

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validaciones
      if (!formData.user_id) {
        setError('Debe seleccionar un propietario');
        setLoading(false);
        return;
      }

      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        setError('El monto debe ser mayor a 0');
        setLoading(false);
        return;
      }

      if (!formData.payment_date) {
        setError('La fecha de pago es obligatoria');
        setLoading(false);
        return;
      }

      // Validar imagen obligatoria para nuevo pago
      if (!payment && !imageFile) {
        setError('El comprobante de pago es obligatorio');
        setLoading(false);
        return;
      }

      // Preparar datos
      // Enviar fecha directamente sin conversión para evitar problemas de zona horaria
      const paymentData = {
        user_id: formData.user_id,
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date, // Ya está en formato YYYY-MM-DD
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        payment_method: formData.payment_method,
        reference_number: formData.reference_number,
      };

      // Agregar imagen si se seleccionó
      if (imageFile) {
        paymentData.payment_proof = imageFile;
      }

      if (payment) {
        // Actualizar pago existente
        await paymentService.update(payment.id, paymentData);
      } else {
        // Crear nuevo pago
        await paymentService.create(paymentData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error al guardar pago';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  const currentYear = new Date().getFullYear();
  // Genera años desde (año actual - searchYearsBack + 1) hasta año actual
  // Ejemplo con searchYearsBack=3: genera [2024, 2025, 2026] si estamos en 2026
  const years = Array.from({ length: searchYearsBack }, (_, i) => currentYear - searchYearsBack + 1 + i);

  const paymentMethods = [
    'Efectivo',
    'ACH',
    'Cheque',
    'Transferencia',
    'Depósito'
  ];

  const needsBank = ['ACH', 'Cheque', 'Transferencia', 'Depósito'].includes(formData.payment_method);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {payment ? 'Editar Pago' : 'Registrar Nuevo Pago'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Propietario"
                name="user_id"
                value={formData.user_id}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Seleccione un propietario</em>
                </MenuItem>
                {owners.map((owner) => (
                  <MenuItem key={owner.id} value={owner.id}>
                    {owner.name} - Casa {owner.house_number}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto"
                name="amount"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.amount}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de Pago"
                name="payment_date"
                type="date"
                value={formData.payment_date}
                onChange={handleChange}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Mes"
                name="month"
                value={formData.month}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Año"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Método de Pago */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Método de Pago"
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {needsBank && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Banco"
                    name="bank_name" // O bank_id si el backend guardara el ID. Usaremos texto para facilitar compatibilidad previa
                    value={formData.reference_number?.split(' - ')[0] || ''}
                    onChange={(e) => {
                      const bank = e.target.value;
                      const ref = formData.reference_number?.split(' - ')[1] || '';
                      setFormData({ ...formData, reference_number: ref ? `${bank} - ${ref}` : bank });
                    }}
                    required={needsBank}
                    disabled={loading}
                  >
                    {banks.map((bank) => (
                      <MenuItem key={bank.id} value={bank.bank_name}>
                        {bank.bank_name}
                      </MenuItem>
                    ))}
                    <MenuItem value="Otro">Otro</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="No. Referencia"
                    name="reference_number_only"
                    value={formData.reference_number?.split(' - ')[1] || (formData.reference_number && !formData.reference_number.includes(' - ') ? formData.reference_number : '')}
                    onChange={(e) => {
                      const ref = e.target.value;
                      const bank = formData.reference_number?.split(' - ')[0] || '';
                      setFormData({ ...formData, reference_number: bank && bank !== 'Otro' ? `${bank} - ${ref}` : ref });
                    }}
                    placeholder="Ej. 12345"
                    required={needsBank}
                    disabled={loading}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 2, textAlign: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="payment-image-upload"
                  type="file"
                  onChange={handleImageChange}
                  disabled={loading}
                />
                <label htmlFor="payment-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={loading}
                  >
                    {imagePreview ? 'Cambiar Comprobante' : 'Cargar Comprobante'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {payment ? 'Opcional al editar' : 'Obligatorio'} - Solo imágenes (JPG, PNG, GIF, WebP) - Máx 5MB
                </Typography>

                {imagePreview && (
                  <Box sx={{ mt: 2 }}>
                    <Avatar
                      src={imagePreview}
                      variant="rounded"
                      sx={{ width: 200, height: 200, mx: 'auto' }}
                    >
                      <ImageIcon />
                    </Avatar>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (payment ? 'Actualizar' : 'Registrar')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PaymentForm;
